import FeatherIcon from 'feather-icons-react';

// Change history for one incident report: field edits (editHistory) and status /
// comment changes (statusHistory) merged into one timeline, newest first.

const STATUS_LABELS = { Open: 'Open', InProgress: 'In Progress', Pending: 'Pending', Resolved: 'Resolved' };
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const formatWhen = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string' && ISO_DATETIME.test(value)) return formatWhen(value) || value;
  return String(value);
};

const whoLabel = (changedBy) => {
  const name = (changedBy?.name || '').trim();
  if (name) return name;
  const role = (changedBy?.role || '').trim();
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Unknown';
};

export const buildIncidentTimeline = (statusHistory = [], editHistory = []) => {
  const events = [];
  (Array.isArray(editHistory) ? editHistory : []).forEach((e) => {
    if (!Array.isArray(e?.changes) || !e.changes.length) return;
    events.push({ kind: 'edit', at: e.timestamp, who: whoLabel(e.changedBy), changes: e.changes });
  });
  const statusAt = (h) => new Date(h?.timestamp || h?.changedBy?.date).getTime();
  const statusEntries = (Array.isArray(statusHistory) ? statusHistory : [])
    .filter((h) => h?.status)
    .sort((a, b) => statusAt(a) - statusAt(b));
  let prevStatus = null;
  statusEntries.forEach((h, idx) => {
    const prev = statusEntries[idx - 1];
    // Older reports recorded each status change twice within the same save; show it once.
    const isEcho = prev && prev.status === h.status && (prev.comments || '') === (h.comments || '')
      && Math.abs(statusAt(h) - statusAt(prev)) < 5000;
    if (isEcho) return;
    events.push({
      kind: 'status',
      at: h.timestamp || h.changedBy?.date,
      who: whoLabel(h.changedBy),
      status: STATUS_LABELS[h.status] || h.status,
      // Entries keep the status even when only the comment moved.
      statusChanged: idx === 0 || h.status !== prevStatus,
      comments: h.comments || '',
    });
    prevStatus = h.status;
  });
  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
};

const styles = {
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  item: { background: '#fff', border: '1px solid rgba(22,59,95,0.12)', borderRadius: 10, padding: '10px 14px' },
  head: { display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: '#163b5f', fontWeight: 700 },
  when: { fontWeight: 500, color: '#6a8ca8' },
  change: { display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 2fr', gap: 8, fontSize: 12.5, padding: '5px 0', borderTop: '1px dashed rgba(22,59,95,0.1)' },
  label: { color: '#3a6b98', fontWeight: 600 },
  from: { color: '#94a3b8', textDecoration: 'line-through', wordBreak: 'break-word' },
  to: { color: '#0f172a', wordBreak: 'break-word' },
  empty: { fontSize: 12.5, color: '#6a8ca8' },
};

export const IncidentReportHistory = ({ statusHistory, editHistory }) => {
  const events = buildIncidentTimeline(statusHistory, editHistory);
  if (!events.length) return <div style={styles.empty}>No changes recorded yet.</div>;

  return (
    <div style={styles.list}>
      {events.map((ev, i) => (
        <div key={`${ev.kind}-${ev.at}-${i}`} style={styles.item}>
          <div style={styles.head}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FeatherIcon icon={ev.kind === 'edit' ? 'edit-3' : 'flag'} size={13} />
              {ev.kind === 'edit'
                ? `${ev.who} edited ${ev.changes.length} field${ev.changes.length === 1 ? '' : 's'}`
                : ev.statusChanged
                  ? `${ev.who} set status to ${ev.status}`
                  : `${ev.who} updated the comment`}
            </span>
            <span style={styles.when}>{formatWhen(ev.at)}</span>
          </div>
          {ev.kind === 'edit' && ev.changes.map((c, j) => (
            <div key={`${c.path}-${j}`} style={styles.change}>
              <span style={styles.label}>{c.label}</span>
              <span>
                <span style={styles.from}>{formatValue(c.from)}</span>
                <FeatherIcon icon="arrow-right" size={11} style={{ margin: '0 6px', color: '#94a3b8' }} />
                <span style={styles.to}>{formatValue(c.to)}</span>
              </span>
            </div>
          ))}
          {ev.kind === 'status' && ev.comments && (
            <div style={{ ...styles.change, gridTemplateColumns: '1fr' }}>
              <span style={styles.to}>Comment: {ev.comments}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
